export class SupportApiError extends Error {
  constructor(message, { status = 0, code = "support_api_error", fields = null } = {}) {
    super(message);
    this.name = "SupportApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

function buildQuery(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}

export function createSupportApi({ baseUrl = "/api/support", csrfToken = null } = {}) {
  const request = async (path, options = {}) => {
    const headers = {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...(options.headers || {}),
    };

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: "include",
      ...options,
      headers,
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = payload.error || {};
      throw new SupportApiError(error.message || "Le support est momentanément indisponible.", {
        status: response.status,
        code: error.code,
        fields: error.fields,
      });
    }

    return payload;
  };

  return {
    async getConfig() {
      return request("/config");
    },

    async listDeals() {
      const payload = await request("/deals");
      return payload.data || [];
    },

    async listPeople() {
      const payload = await request("/people");
      return payload.data || [];
    },

    async unreadCount() {
      const payload = await request("/unread-count");
      return Number(payload.count || 0);
    },

    async listTickets(params = {}) {
      return request(`/tickets${buildQuery(params)}`);
    },

    async getTicket(id) {
      const payload = await request(`/tickets/${id}`);
      return payload.data;
    },

    async createTicket(ticket) {
      const attachments = Array.isArray(ticket.attachments) ? ticket.attachments : [];
      const ticketPayload = { ...ticket };
      delete ticketPayload.attachments;

      const payload = await request("/tickets", {
        method: "POST",
        body: JSON.stringify({ ticket: ticketPayload }),
      });

      const createdTicket = payload.data;
      const attachmentUploadErrors = [];

      for (const file of attachments) {
        try {
          await request(`/tickets/${createdTicket.id}/attachments`, {
            method: "POST",
            body: file,
            headers: {
              "Content-Type": file.type || "application/octet-stream",
              "x-support-file-name": encodeURIComponent(file.name || "document"),
            },
          });
        } catch (error) {
          attachmentUploadErrors.push(`${file.name || "Document"} : ${error.message}`);
        }
      }

      let refreshedTicket = createdTicket;

      if (attachments.length) {
        const refreshedPayload = await request(`/tickets/${createdTicket.id}`);
        refreshedTicket = refreshedPayload.data;
      }

      return {
        ...refreshedTicket,
        attachment_upload_errors: attachmentUploadErrors,
      };
    },

    async reply(id, message) {
      const payload = await request(`/tickets/${id}/messages`, {
        method: "POST",
        body: JSON.stringify({ message }),
      });
      return payload.data;
    },
  };
}
